/**
 * Displays information regarding the certificates ( labs includes, duration, description, etc)
 * when the user plan is free click on th 'buy' button to go to checkout area -> checkout session (stripe) -> subscribe to the Cert.
 * when the user plan is enterprise the 'buy' button changes to 'enroll' button and the cert is allocated to the user (no payment)
 */
import Styles from './LabInfo.module.scss';
import { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Module imports
import axios from 'axios';
import { Typography, Drawer, Tooltip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { toast, ToastContainer } from 'react-toastify';
import { Tooltip as ReactTooltip } from "react-tooltip";

// Components import.
import { useAPI, AuthContext, useCan } from 'components/lib';
import { BreadCrumbs } from 'components/global/BreadCrumbs/BreadCrumbs';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import { Checkout } from 'components/global/Checkout/Checkout';
import { Notification } from 'components/notifications/notification';
import { CalendarTodayOutlined, HourglassBottomOutlined, InfoOutlined, MenuBookOutlined, WorkspacePremiumOutlined } from '@mui/icons-material';
import { InfoNotice } from 'components/global/InfoNotice/InfoNotice';

export function LabInfo(props) {

    const [labList, setLabList] = useState([]);
    const [moduleList, setModuleList] = useState([]);
    const [showLabDescr, setShowLabDescr] = useState();
    const [showCheckOut, setShowCheckout] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [calcPrices, setCalcPrices] = useState({ discount: '', strikedTotal: '', subTotal: '', currencySymbol: '', interval: '' });
    const [buyLoading, setBuyLoading] = useState(false);

    const authContext = useContext(AuthContext);
    const navigate = useNavigate();
    // eslint-disable-next-line
    const { lab_id } = useParams();

    const { data, loading } = useAPI(`/api/labs/${lab_id}/nodes`, 'GET');
    const currencySymbol = authContext?.user.currencySymbol;
    const additionalMessage = labList?.additionalMessage?.message;
    const messageType = labList?.additionalMessage?.type;

    const isScholar = useCan('user.months.view');

    //receiving the lab data
    useEffect(() => {
        if (data) {
            setModuleList(data.nodes);
            setLabList(data.lab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data])

    useEffect(() => {
        if (labList?.pricing) {
            const price = labList?.pricing;
            const strikedTotal = price?.actual_price;
            const subTotal = price?.price;

            const discount = strikedTotal - subTotal
            setCalcPrices({
                currencySymbol,
                discount: discount,
                strikedTotal: strikedTotal,
                subTotal: subTotal,
                interval: price?.interval
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [labList?.pricing]);

    const handleEnroll = (isTrial) => {
        const data = {
            labIds: [lab_id]
        }
        const options = {
            method: 'POST',
            url: `/api/labs/enroll`,
            params: {
                isTrial : isTrial ? 1 : 0,
            },
            headers: {
                accept: 'application/json',
            },
            data: data,
        };

        setBuyLoading(true);

        axios
            .request(options)
            .then(function (response) {
                let status = response.status;
                if (status === 201) {
                    toast.dark(props.t('Labs.LabInfo.toast.success'), {
                        type: 'success',
                        position: 'top-right',
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                    labList.isProject === 1 ? navigate('/projects') : navigate('/labs');
                } else {
                    toast.dark(props.t('Labs.LabInfo.toast.failed'), {
                        type: 'error',
                        position: 'top-right',
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });

                }
            })
            .catch((err) => {
                toast.dark(props.t('Labs.LabInfo.toast.error'), {
                    type: 'error',
                    position: 'top-right',
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });

            })
            .finally(() => setBuyLoading(false));
    }

    return (
        <>
            <ToastContainer
                id="warning-toast"
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
            <Notification call='cert' />
            <Typography
                variant="h6"
                sx={{ display: 'flex', gap: '16px', padding: '16px', mb: '-15px', color: 'rgb(0, 74, 173)' }}
            >
                <BreadCrumbs
                    onClickPaths={[
                        labList.isProject ? '/projects' : '/labs',
                        ''
                    ]}
                    crumbs={[
                        props.t(
                            labList.isProject
                                ? 'Projects'
                                : 'Courses'
                        ),
                        props.t(
                            labList.isProject
                                ? 'Labs.LabInfo.breadcrumbs.project.curr'
                                : 'Labs.LabInfo.breadcrumbs.course.curr',
                            { lab: labList.name }
                        )
                    ]}

                />
            </Typography>
            <div >
                <Drawer anchor='right' open={showCheckOut} onClose={() => { setShowCheckout(!showCheckOut) }}
                    PaperProps={{
                        sx: {
                            backgroundColor: 'transparent',
                        }
                    }}
                >
                    <div className={Styles.checkoutDrawer}>
                        <Checkout
                            purchaseEndpoint='/api/labs/purchase'
                            checkoutName='course'
                            availableItems={[labList]}
                            onClose={() => setShowCheckout(false)}
                            isProject={labList.isProject}
                        />
                    </div>
                </Drawer>
            </div>
            {(loading || buyLoading) ? (
                <div>
                    <LoadingBackdrop isOpen={true} />
                </div>
            ) : (
                <div className={Styles.cardContainer}>
                    <div className={Styles.courseDetails}>
                        <div className={Styles.imageContainer} >
                            <img src={`${process.env.REACT_APP_GLOBAL_MEDIA_URL}${labList.icon}`} className={Styles.courseImage} alt="certificate" />
                        </div>
                        <p className={Styles.courseName}>{labList.name}</p>
                        <p className={Styles.courseDescr}>{labList.descr}</p>
                        <div className={Styles.pricingDesign}>
                            <InfoNotice message={additionalMessage} type={messageType} />
                            <Typography variant='h5' sx={{ fontWeight: 600 }}>
                                Get Started Today
                            </Typography>
                            {(authContext?.user?.plan === 'free' || (authContext?.user.plan === 'hybrid' && (labList?.orgPurchaseStatus !== 'active'))) && (
                                <div style={{ marginTop: '0.5em', display: 'flex', gap: '1em', alignItems: 'center', padding: '0.5em', width: '100%' }}>
                                    <Typography variant='h6' sx={{ textDecoration: 'line-through', color: 'darkgray' }}>{calcPrices.currencySymbol} {calcPrices.strikedTotal}</Typography>
                                    <Typography variant='h4' sx={{ fontWeight: 600 }}> {calcPrices.currencySymbol} {calcPrices.subTotal}</Typography>
                                    <span id='itemSaving' className={Styles.itemSaving}>Save {calcPrices.currencySymbol} {calcPrices.discount}</span>
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '1em', width: '100%', alignItems: 'center', marginTop: '1em' }}>
                                {(authContext?.user?.plan === 'free' || (authContext?.user.plan === 'hybrid' && (labList?.orgPurchaseStatus !== 'active'))) ? (
                                    <>
                                        <button className={Styles.purchaseBtn} onClick={() => { setShowCheckout(!showCheckOut) }}>
                                            Buy now
                                        </button>
                                        {!['trial', 'trial_expired', 'expired', 'disabled'].includes(labList?.subscriptionStatus) && (
                                            <button className={Styles.trialBtn} onClick={() => { handleEnroll(true) }}>
                                                Free Trial
                                                <Tooltip title='The first module of the course can be tried for free' arrow placement='top'>
                                                    <InfoOutlined fontSize='small' sx={{ ml: '0.5em' }} />
                                                </Tooltip>
                                            </button>
                                        )}
                                    </>
                                ) : (
                                    <button className={Styles.enrollBtn} onClick={() => { handleEnroll(false) }}>
                                        Enroll Now
                                    </button>
                                )}

                            </div>
                        </div>
                        <div style={{ fontSize: "1em", color: '#1450a0', marginTop: '1.5em', display: 'flex', gap: '1.5em', alignItems: 'center' }}>
                            <span style={{ display: 'flex', gap: '0.25em', alignItems: 'center' }}><MenuBookOutlined fontSize='small' /> {moduleList?.length} Modules </span>
                            <span style={{ display: 'flex', gap: '0.25em', alignItems: 'center' }}><HourglassBottomOutlined fontSize='small' /> {Math.ceil(((60 * (labList.isProject ? 2 : 1)) * moduleList?.length) / 60)} Hours </span>
                            <span style={{ display: 'flex', gap: '0.25em', alignItems: 'center' }}><WorkspacePremiumOutlined fontSize='small' /> Micro Certificate</span>
                            {isScholar && (
                                <span style={{ display: 'flex', gap: '0.25em', alignItems: 'center' }}><CalendarTodayOutlined fontSize='small' /> {calcPrices?.interval} Months Access </span>
                            )}
                        </div>
                    </div>

                    <div className={Styles.labsInclude}>
                        <p style={{ fontSize: "30px", fontWeight: 700, color: 'black' }}>{props.t('Labs.LabInfo.modules.include')}</p>
                        <p style={{ fontSize: '14px', color: "grey", marginTop: '-10px' }}>{props.t('Labs.LabInfo.modules.overview')}</p>
                        <div style={{ width: "100%", backgroundColor: '#E5E7E8', height: '1.5px', marginTop: '15px', marginBottom: '15px' }} />
                        <div className={Styles.labListContainer}>
                            {moduleList?.map((lab, index) => {
                                return (
                                    <div
                                        className={Styles.labList}
                                        key={lab.id + index}
                                        data-tooltip-id={((authContext?.user?.plan === 'free' || (authContext?.user.plan === 'hybrid' && (!labList?.org_purchase || labList?.status !== 'active'))) && lab.isEnrolled) && "my-tooltip-1"}
                                        style={{
                                            height: 'fit-content',
                                            backgroundColor: ((authContext?.user?.plan === 'free' || (authContext?.user.plan === 'hybrid' && (!labList?.org_purchase || labList?.status !== 'active'))) && lab.isEnrolled) ? "	#D3D3D3" : "white",
                                            filter: ((authContext?.user?.plan === 'free' || (authContext?.user.plan === 'hybrid' && (!labList?.org_purchase || labList?.status !== 'active'))) && lab.isEnrolled) ? "saturate(0)" : "none",
                                        }}
                                    >
                                        <div style={{ display: 'flex' }}>
                                            <img src={`${process.env.REACT_APP_GLOBAL_MEDIA_URL}${lab.icon}`} className={Styles.labsImg} alt={props.t('Labs.LabInfo.altIcon')} />
                                            <span
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    fontSize: '18px', color: 'black',
                                                    fontWeight: 'bold',
                                                    padding: '10px',
                                                    width: '100%',
                                                    justifyContent: 'space-between'
                                                }}
                                            >
                                                <span>
                                                    <p>{lab.name}</p>
                                                </span>
                                                <button style={{ height: '100%' }} onClick={() => { showLabDescr !== lab.id ? setShowLabDescr(lab.id) : setShowLabDescr(null) }} ><KeyboardArrowDownIcon /></button>
                                            </span>
                                        </div>
                                        <div className={Styles.labsDescr} hidden={showLabDescr !== lab.id}>
                                            {lab.descr}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            )}
            <ReactTooltip
                id="my-tooltip-1"
                place="left"
                content={props.t('Labs.LabInfo.message')}
            />
        </>
    );
}
